import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { AlertCircle } from '@lifeomic/chromicons';
import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isAiGenerated } from '../../ExecutiveSummary/utils';
import { useTemplatesContext } from './Context';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { ImpressionItem } from '../ImpressionEditor/ImpressionItem';
import { apiHooks } from '../../../../../hooks/useRestAPIHooks';
import { ModelSelect } from '../../../../AITemplates/GenerationMenu/ModelSelect';
import {
  TemplateExecutionMode,
  ToolsLoopExecutionSelect,
} from '../../../../AITemplates/GenerationMenu/ToolsLoopExecutionSelect';
import {
  CitationPostProcessingOption,
  useRemoveAllCitationsPreference,
} from '../../../../AITemplates/GenerationMenu/CitationPostProcessingOption';

type GenerateModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  templateId: string;
  activeImpression?: ImpressionItem | undefined;
};

export const messages = defineMessages({
  generate: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.templates.generateModal.generate',
    defaultMessage: 'Generate',
  },
  regenerate: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.templates.generateModal.regenerate',
    defaultMessage: 'Regenerate',
  },
  cancel: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.templates.generateModal.cancel',
    defaultMessage: 'Cancel',
  },
  regenerateAlert: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.regenerateAlert',
    defaultMessage:
      'This version of the report is completed. Generating will create a new version.',
  },
  aiGeneratedAlert: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.aiGeneratedAlert',
    defaultMessage:
      'This report has been edited by a person. Regenerating will create a new copy and leave the edited version as is.',
  },
  instructions: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.instructions',
    defaultMessage: 'Instructions (optional)',
  },
  placeholderInstructions: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.placeholderInstructions',
    defaultMessage:
      'Enter any additional instructions for the report (re)generation (e.g., "Output in Spanish" or reference a specific body part).',
  },
  generateSuccess: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.generateSuccess',
    defaultMessage:
      'Report regeneration started. This page will refresh when the report is ready.',
  },
  generateFailure: {
    id: 'phc-web-toolkit.subjectViewer.aiTools.controlBar.generateFailure',
    defaultMessage:
      'Failed to regenerate content. Please try again or notify support.',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  regenerateAlert: {
    margin: theme.spacing(0, 1, 1),
    padding: theme.spacing(1, 1.25),
  },
  alertIcon: {
    marginRight: theme.spacing(0.75),
    maxWidth: theme.pxToRem(18),
    maxHeight: theme.pxToRem(18),
    minWidth: theme.pxToRem(18),
    minHeight: theme.pxToRem(18),
  },
}));

export type GenerateFormState = {
  instructions: string | undefined;
  setInstructions: (value: string | undefined) => void;
  modelId: string | undefined;
  setModelId: (value: string | undefined) => void;
  executionMode: TemplateExecutionMode;
  setExecutionMode: (value: TemplateExecutionMode) => void;
  removeAllCitations: boolean;
  setRemoveAllCitations: (value: boolean) => void;
  isRequesting: boolean;
  isGenerating: boolean;
  handleGenerate: () => void;
  showModelSelect: boolean;
  models: { model_id: string; model_label: string }[];
};

export function useGenerateForm({
  templateId,
  onDismiss,
  activeImpression,
}: {
  templateId: string;
  onDismiss: () => void;
  activeImpression?: ImpressionItem | undefined;
}): GenerateFormState {
  const { generateReport, invocations } = useTemplatesContext();
  const isGenerating = useMemo(() => {
    return invocations.some(
      invocation =>
        invocation.status === 'processing' &&
        invocation.agentInput.template_id === templateId,
    );
  }, [invocations, templateId]);

  const [instructions, setInstructions] = useState<string | undefined>(
    undefined,
  );
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const [executionMode, setExecutionMode] =
    useState<TemplateExecutionMode>('tools_loop');
  const [removeAllCitations, setRemoveAllCitations] =
    useRemoveAllCitationsPreference();
  const modelsQuery = apiHooks.useAPIQuery('GET /v1/agents-v2/models', {}) as {
    data?: {
      models: { model_id: string; model_label: string }[];
    };
  };
  const showModelSelect = true;

  const [isRequesting, setIsRequesting] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (isGenerating || isRequesting) {
      return;
    }

    setIsRequesting(true);

    generateReport({
      templateId,
      instructions,
      modelId,
      toolsLoop: executionMode === 'tools_loop',
      postProcessRemoveAllCitations: removeAllCitations,
      isRegenerate: !!activeImpression,
      onSuccess: () => {
        setInstructions(undefined);
        onDismiss();
        setIsRequesting(false);
      },
      onError: () => {
        setIsRequesting(false);
      },
    });
  }, [
    isGenerating,
    templateId,
    instructions,
    modelId,
    executionMode,
    removeAllCitations,
    generateReport,
    onDismiss,
    isRequesting,
    activeImpression,
  ]);

  return {
    instructions,
    setInstructions,
    modelId,
    setModelId,
    executionMode,
    setExecutionMode,
    removeAllCitations,
    setRemoveAllCitations,
    isRequesting,
    isGenerating,
    handleGenerate,
    showModelSelect,
    models: modelsQuery.data?.models ?? [],
  };
}

export const GenerateFormBody: React.FC<{
  form: GenerateFormState;
  activeImpression?: ImpressionItem | undefined;
}> = ({ form, activeImpression }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  return (
    <Box direction="column" gap={2}>
      {activeImpression?.status === 'completed' ? (
        <Alert
          key="regenerateAlert"
          className={classes.regenerateAlert}
          statusType="warning"
          fullWidth
        >
          <AlertIcon icon={AlertCircle} className={classes.alertIcon} />
          <AlertBody>
            <Text size="caption" weight="bold">
              {formatMessage(messages.regenerateAlert)}
            </Text>
          </AlertBody>
        </Alert>
      ) : activeImpression && !isAiGenerated(activeImpression?.impression) ? (
        <Alert
          key="regenerateAlert"
          className={classes.regenerateAlert}
          statusType="warning"
          fullWidth
        >
          <AlertIcon icon={AlertCircle} className={classes.alertIcon} />
          <AlertBody>
            <Text size="caption" weight="bold">
              {formatMessage(messages.aiGeneratedAlert)}
            </Text>
          </AlertBody>
        </Alert>
      ) : null}
      <TextArea
        style={{ resize: 'none' }}
        label={formatMessage(messages.instructions)}
        placeholder={formatMessage(messages.placeholderInstructions)}
        value={form.instructions ?? ''}
        onChange={content => form.setInstructions(content.target.value)}
        fullWidth
        rows={10}
      />
      {form.showModelSelect && (
        <ModelSelect
          modelId={form.modelId}
          setModelId={form.setModelId}
          models={form.models}
        />
      )}
      <ToolsLoopExecutionSelect
        value={form.executionMode}
        onChange={form.setExecutionMode}
      />
      <CitationPostProcessingOption
        checked={form.removeAllCitations}
        onChange={form.setRemoveAllCitations}
      />
    </Box>
  );
};

export const GenerateModal = ({
  isOpen,
  onDismiss,
  templateId,
  activeImpression,
}: GenerateModalProps) => {
  const { formatMessage } = useIntl();
  const form = useGenerateForm({ templateId, onDismiss, activeImpression });

  return (
    <Modal
      size={1}
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={formatMessage(
        activeImpression ? messages.regenerate : messages.generate,
      )}
      actions={[
        <Button key="cancel" variant="outlined" onClick={onDismiss}>
          {formatMessage(messages.cancel)}
        </Button>,
        <Button
          key="generate"
          onClick={form.handleGenerate}
          disabled={form.isGenerating || form.isRequesting}
        >
          {formatMessage(
            activeImpression ? messages.regenerate : messages.generate,
          )}
        </Button>,
      ]}
    >
      <GenerateFormBody form={form} activeImpression={activeImpression} />
    </Modal>
  );
};
